import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Instagram, Mail, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { config } from '@/config/env';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useI18n } from '@/hooks/useI18n';
import { mailtoFallback } from '@/services/api/leadService';
import type { LeadPayload, LeadType } from '@/types/leads';
import styles from './ContactPage.module.css';

/**
 * El formulario redacta un correo; no envía nada a ningún servidor.
 *
 * Hasta el 2026-09-18 tenía dos caminos: si había API configurada hacía `POST
 * /leads`, y si no caía a `mailto:`. El sitio se quedó sin backend, así que el
 * primero se fue y el segundo dejó de ser un respaldo para ser **el**
 * comportamiento. Ya no hay bandera que consultar ni entorno donde esto cambie.
 *
 * Lo que eso obliga a decir bien: `window.location.href = "mailto:…"` **no
 * hace nada visible** en un equipo sin cliente de correo configurado —y en una
 * oficina donde el correo se lee en una pestaña del navegador, eso es lo
 * normal—. Si la pantalla dijera sólo «abrimos tu cliente de correo», quien
 * está en ese caso se queda mirando un formulario que parece haber funcionado.
 * Por eso el aviso de éxito nombra la dirección y dice qué hacer si no pasó
 * nada.
 */

/** El `value` viaja al backend y no se traduce; la etiqueta sí. */
const LEAD_TYPES: LeadType[] = ['demo', 'contacto', 'loi'];
const PERFILES = ['marca', 'organizador', 'otro'] as const;

interface FormState {
  nombre: string;
  email: string;
  empresa: string;
  telefono: string;
  perfil: string;
  mensaje: string;
  website: string;
}

const EMPTY_FORM: FormState = {
  nombre: '',
  email: '',
  empresa: '',
  telefono: '',
  perfil: 'marca',
  mensaje: '',
  website: '',
};

type Status = { kind: 'idle' } | { kind: 'ok'; message: string };

/** Estado que puede llegar desde los CTA de la sección de planes. */
interface PlanNavigationState {
  /** Identificadores estables; son los que se guardan en el lead. */
  planId?: string;
  variantId?: string;
  /** Etiquetas ya traducidas, para enseñarlas y para el cuerpo del correo. */
  plan?: string;
  variant?: string;
  tipo?: LeadType;
}

type ErroresDeForma = Partial<Record<keyof FormState, string>>;

function validate(form: FormState, mensajes: Record<string, string>): ErroresDeForma {
  const errors: ErroresDeForma = {};
  if (!form.nombre.trim()) errors.nombre = mensajes.nombre;
  if (!form.empresa.trim()) errors.empresa = mensajes.empresa;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
    errors.email = mensajes.email;
  }
  if (form.mensaje.trim().length < 10) {
    errors.mensaje = mensajes.mensaje;
  }
  return errors;
}

export function ContactPage() {
  const { t, idioma } = useI18n();
  const c = t.contacto;

  useDocumentMeta(t.meta.contacto.title, t.meta.contacto.description);

  const navState = (useLocation().state ?? {}) as PlanNavigationState;
  const planInteres = navState.plan
    ? [navState.plan, navState.variant].filter(Boolean).join(' — ')
    : undefined;

  const [tipo, setTipo] = useState<LeadType>(navState.tipo ?? (navState.plan ? 'demo' : 'contacto'));
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<ErroresDeForma>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function buildPayload(): LeadPayload {
    return {
      tipo,
      nombre: form.nombre.trim(),
      email: form.email.trim(),
      empresa: form.empresa.trim(),
      telefono: form.telefono.trim() || undefined,
      perfil: form.perfil,
      planInteres,
      // Los identificadores van además de la etiqueta: la etiqueta viaja
      // traducida y no sirve para agrupar (ver `types/leads.ts`).
      planId: navState.planId,
      variantId: navState.variantId,
      idioma,
      mensaje: form.mensaje.trim(),
      website: form.website,
    };
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validate(form, c.errores);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    // Si el honeypot trae contenido, quien llenó el formulario fue un bot.
    // Se finge éxito para no darle señal de qué falló y no se abre nada.
    if (form.website.trim()) {
      setStatus({ kind: 'ok', message: c.estados.graciasBot });
      setForm(EMPTY_FORM);
      return;
    }

    // No se limpia el formulario: si el cliente de correo no abrió, lo escrito
    // es lo único que esa persona tiene, y borrárselo sería perderlo.
    window.location.href = mailtoFallback(buildPayload(), c.mailto);
    setStatus({ kind: 'ok', message: c.estados.mailtoAbierto(config.contactEmail) });
  }

  return (
    <div className={styles.page}>
      <div className={['ilu-container', styles.grid].join(' ')}>
        <div>
          <span className="ilu-eyebrow">{c.eyebrow}</span>
          <h1 className={styles.title}>{c.titulo}</h1>
          <p className={styles.lead}>{c.lead}</p>

          {planInteres && (
            <p className={styles.lead}>
              {c.planInteres} <strong>{planInteres}</strong>
            </p>
          )}

          <ul className={styles.contactList}>
            <li className={styles.contactItem}>
              <Mail size={18} aria-hidden="true" />
              <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>
            </li>
            <li className={styles.contactItem}>
              <Instagram size={18} aria-hidden="true" />
              <a href={config.instagramUrl} target="_blank" rel="noreferrer noopener">
                @ilumetriamx
              </a>
            </li>
            <li className={styles.contactItem}>
              <MessageSquare size={18} aria-hidden="true" />
              <span>{c.horario}</span>
            </li>
          </ul>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.typeTabs} role="group" aria-label={c.tipoSolicitud}>
            {LEAD_TYPES.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={tipo === value}
                onClick={() => setTipo(value)}
              >
                {c.tipos[value]}
              </button>
            ))}
          </div>

          {status.kind !== 'idle' && (
            <p className={[styles.status, styles.statusOk].join(' ')} role="status">
              {status.message}
            </p>
          )}

          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="nombre">{c.campos.nombre}</label>
              <input
                id="nombre"
                name="nombre"
                autoComplete="name"
                value={form.nombre}
                onChange={(event) => update('nombre', event.target.value)}
                aria-invalid={Boolean(errors.nombre)}
              />
              {errors.nombre && <p className={styles.error}>{errors.nombre}</p>}
            </div>

            <div className={styles.field}>
              <label htmlFor="empresa">{c.campos.empresa}</label>
              <input
                id="empresa"
                name="empresa"
                autoComplete="organization"
                value={form.empresa}
                onChange={(event) => update('empresa', event.target.value)}
                aria-invalid={Boolean(errors.empresa)}
              />
              {errors.empresa && <p className={styles.error}>{errors.empresa}</p>}
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="email">{c.campos.email}</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => update('email', event.target.value)}
                aria-invalid={Boolean(errors.email)}
              />
              {errors.email && <p className={styles.error}>{errors.email}</p>}
            </div>

            <div className={styles.field}>
              <label htmlFor="telefono">{c.campos.telefono}</label>
              <input
                id="telefono"
                name="telefono"
                type="tel"
                autoComplete="tel"
                value={form.telefono}
                onChange={(event) => update('telefono', event.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label htmlFor="perfil">{c.perfilPregunta}</label>
            <select
              id="perfil"
              name="perfil"
              value={form.perfil}
              onChange={(event) => update('perfil', event.target.value)}
            >
              {PERFILES.map((value) => (
                <option key={value} value={value}>
                  {c.perfiles[value]}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="mensaje">{c.campos.mensaje}</label>
            <textarea
              id="mensaje"
              name="mensaje"
              rows={5}
              placeholder={c.campos.mensajePlaceholder}
              value={form.mensaje}
              onChange={(event) => update('mensaje', event.target.value)}
              aria-invalid={Boolean(errors.mensaje)}
            />
            {errors.mensaje && <p className={styles.error}>{errors.mensaje}</p>}
          </div>

          <div className={styles.honeypot} aria-hidden="true">
            <label htmlFor="website">{c.campos.honeypot}</label>
            <input
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(event) => update('website', event.target.value)}
            />
          </div>

          <Button type="submit" block>
            {c.enviar}
          </Button>

          {/* Antes del botón no: quien ya sabe lo que hace el formulario no
              necesita leerlo dos veces. Pero sí antes de pulsarlo, porque
              cambia qué esperar — no es un envío, es un borrador. */}
          <p className={styles.nota}>
            {c.porCorreo.aviso}{' '}
            <a href={`mailto:${config.contactEmail}`}>{config.contactEmail}</a>
          </p>
        </form>
      </div>
    </div>
  );
}
